import { AlertTriangle, X, CheckCircle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AppColors } from '../types/theme';
import type { FeedbackUpdateNotification } from './useFeedbackNotifications';

interface Props {
  notification: FeedbackUpdateNotification;
  colors: AppColors;
  onDismiss: () => void;
}

const STATUS_ICON: Record<FeedbackUpdateNotification['status'], typeof CheckCircle> = {
  open: Clock,
  in_progress: Clock,
  resolved: CheckCircle,
  closed: CheckCircle,
};

export function FeedbackUpdateModal({ notification, colors, onDismiss }: Props) {
  const { t } = useTranslation('common');
  const isCritical = notification.priority === 'critical';
  const StatusIcon = STATUS_ICON[notification.status];
  // Render the full admin_notes thread (server stores it as a newline-joined
  // log of "[YYYY-MM-DD HH:MM email] note" lines).
  const noteLines = notification.admin_notes
    ? notification.admin_notes.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
    : [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div
        className="rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
        style={{ backgroundColor: colors.card_bg_color }}
      >
        <div
          className="flex items-start justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${colors.border_color}` }}
        >
          <div className="flex items-start gap-3 min-w-0">
            {isCritical ? (
              <div className="p-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#fee2e2' }}>
                <AlertTriangle size={20} style={{ color: '#dc2626' }} />
              </div>
            ) : (
              <div className="p-2 rounded-full flex-shrink-0" style={{ backgroundColor: colors.hover_bg_color }}>
                <StatusIcon size={20} style={{ color: colors.primary_color }} />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide font-semibold mb-0.5" style={{ color: colors.text_secondary_color }}>
                {t(`notifications.modal.heading.${notification.type}`)}
              </div>
              <h2 className="text-lg font-semibold" style={{ color: colors.text_primary_color }}>
                {notification.title}
              </h2>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-lg transition-colors flex-shrink-0"
            style={{ color: colors.text_secondary_color }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.hover_bg_color)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label={t('actions.close')}
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="flex items-center gap-2 mb-4">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={statusChipStyle(notification.status, colors)}
            >
              <StatusIcon size={12} />
              {t(`notifications.status.${notification.status}`)}
            </span>
            {isCritical && (
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
              >
                <AlertTriangle size={12} />
                {t('notifications.priority.critical')}
              </span>
            )}
          </div>

          {noteLines.length === 0 ? (
            <p className="text-sm" style={{ color: colors.text_secondary_color }}>
              {t('notifications.modal.noNotes')}
            </p>
          ) : (
            <div>
              <div className="text-xs uppercase tracking-wide font-semibold mb-2" style={{ color: colors.text_secondary_color }}>
                {t('notifications.modal.thread')}
              </div>
              <div className="space-y-2">
                {noteLines.map((line, i) => (
                  <div
                    key={i}
                    className="text-sm rounded-lg px-3 py-2"
                    style={{ backgroundColor: colors.hover_bg_color, color: colors.text_primary_color }}
                  >
                    {line}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div
          className="px-6 py-3 flex justify-end flex-shrink-0"
          style={{ borderTop: `1px solid ${colors.border_color}` }}
        >
          <button
            onClick={onDismiss}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
            style={{ backgroundColor: colors.primary_color }}
          >
            {t('notifications.modal.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
}

function statusChipStyle(status: FeedbackUpdateNotification['status'], colors: AppColors): React.CSSProperties {
  switch (status) {
    case 'resolved':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'closed':
      return { backgroundColor: colors.hover_bg_color, color: colors.text_secondary_color };
    case 'in_progress':
      return { backgroundColor: '#dbeafe', color: '#1e40af' };
    default:
      return { backgroundColor: colors.hover_bg_color, color: colors.text_primary_color };
  }
}
